import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import ja from '../../messages/ja.json';
import en from '../../messages/en.json';
import ko from '../../messages/ko.json';
import type { Locale } from '../types';

const DICTS: Record<Locale, Record<string, string>> = { ja, en, ko };

interface I18nContextValue {
  locale: Locale;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

/** ブラウザの言語 (navigator.language / Accept-Language) から locale を決定。
 * メインユーザーは英語想定なのでデフォルトは英語。 */
function resolveLocale(input?: string | null): Locale {
  if (input === 'ja' || input === 'en' || input === 'ko') return input;
  const nav = (input ?? (typeof navigator !== 'undefined' ? navigator.language : null) ?? 'en')
    .toLowerCase();
  if (nav.startsWith('ja')) return 'ja';
  if (nav.startsWith('ko')) return 'ko';
  return 'en';
}

interface ProviderProps {
  locale?: string | null;
  children: ReactNode;
}

export function I18nProvider({ locale: localeProp, children }: ProviderProps) {
  const value = useMemo<I18nContextValue>(() => {
    const locale = resolveLocale(localeProp);
    const dict = DICTS[locale];
    return {
      locale,
      t: (key: string) => dict[key] ?? key,
    };
  }, [localeProp]);

  // <html lang> をブラウザ言語に同期 (SEO / アクセシビリティ / ブラウザ翻訳の精度向上)
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = value.locale;
    }
  }, [value.locale]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
